// Chat-Modul – REST-API-Service
// All requests use 'locationId' + 'supplierId' (NO 'roomId' in the URL)

import { useApi } from "@/composables/useApi";
import type { ApiRoom, ApiMessage } from "@/types/chat";

const { api } = useApi();
export const MESSAGE_PAGE_SIZE = 30;

interface ApiResponse<T> {
    count: number;
    data: T;
}

export const chatApi = {
    // all location ↔ supplier pair rooms (overview)
    async getRooms(locationId: string): Promise<ApiRoom[]> {
        const { data } = await api.get<ApiResponse<ApiRoom[]>>(
            `/locations/${locationId}/chat/rooms`
        );
        return data.data;
    },

    /**
     * Message history for a room (paginated).
     * @param skip Number of messages to skip, counting backward from the NEWEST. 0 = latest page
     * @param limit Number of messages on the page
     */
    async getMessages(
        locationId: string,
        supplierId: string,
        skip = 0,
    ): Promise<ApiMessage[]> {
        const { data } = await api.get<ApiResponse<ApiMessage[]>>(
            `/locations/${locationId}/chat/${supplierId}/messages`,
            { params: { skip, limit: MESSAGE_PAGE_SIZE } },
        );
        return data.data.reverse();
    },

    // send message – optionally with attachments (multipart/form-data)
    async sendMessage(
        locationId: string,
        supplierId: string,
        text: string,
        files: File[] = [],
    ): Promise<ApiMessage> {
        const form = new FormData();
        form.append("body", text);
        files.forEach((f) => form.append("attachments", f, f.name));

        const { data } = await api.post<ApiMessage>(
            `/locations/${locationId}/chat/${supplierId}/messages`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } },
        );
        return data;
    },

    // mark messages as read
    async markAsRead(
        locationId: string,
        supplierId: string,
        messageIds: string[],
    ): Promise<void> {
        if (!messageIds.length) return;
        await api.post(`/locations/${locationId}/chat/${supplierId}/read`, {
            messageIds,
        });
    },

    // check room → return the existing one or create a new one 
    async ensureRoom(locationId: string, supplierId: string): Promise<ApiRoom> {
        const { data } = await api.post<ApiRoom>(
            `/chat/rooms/${locationId}/${supplierId}/ensure`,
        );
        return data;
    },
};
